import API from "../api";
import React, { useEffect, useState, useRef, useMemo } from "react"; 
import { Link, useLocation, useNavigate } from "react-router-dom"; 
import { Toaster, toast } from "react-hot-toast"; 
import ProductCard from "../components/ProductCard"; 
import SkeletonCard from "../components/SkeletonCard"; 
import { Carousel } from "react-responsive-carousel"; 
import "react-responsive-carousel/lib/styles/carousel.min.css"; 

const categoryIcons = {
  "All": "🏠", "Vegetables": "🥦", "Fruits": "🍎", "Dairy": "🥛",
  "Snacks": "🍟", "Drinks": "🥤", "Bakery": "🍞", "Essentials": "🧼",
  "Dry Fruits/Seeds": "📦", "Cookies": "🍪", "Spices": "🌶️", "Meat": "🥩",
  "Personal Care": "🧴", "Cleaning": "🧹"
};

const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

const shuffleArray = (array) => {
  let shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function Shop({ cart, setCart }) {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [combos, setCombos] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(""); 
  const [category, setCategory] = useState("All");
  const [showDrawer, setShowDrawer] = useState(false);
  
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(true);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestionPool, setSuggestionPool] = useState([]);
  
  const masterPoolRef = useRef([]);
  const feedRef = useRef(null); 

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isStoreOpen, setIsStoreOpen] = useState(true); 
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const location = useLocation();
  const navigate = useNavigate(); 

  useEffect(() => {
    if (cart.length === 0) {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) setCart(JSON.parse(savedCart));
    }
  }, [cart.length, setCart]);

  useEffect(() => {
    const handleOpenDrawer = () => {
      setShowDrawer(true);
      setIsSuggestionsVisible(true);
    };
    window.addEventListener('openCartDrawer', handleOpenDrawer);
    return () => window.removeEventListener('openCartDrawer', handleOpenDrawer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [bRes, sRes, poolRes, comboRes] = await Promise.all([
          API.get("/banners").catch(() => ({ data: [] })),
          API.get("/settings/status").catch(() => ({ data: { isOpen: true } })),
          API.get("/products?limit=100&isVisible=true").catch(() => ({ data: [] })),
          API.get("/combos").catch(() => ({ data: [] }))
        ]);
        
        setBanners(Array.isArray(bRes.data) ? bRes.data : []);
        setIsStoreOpen(sRes.data.isOpen);
        setCombos(Array.isArray(comboRes.data) ? comboRes.data : []); 

        const poolItems = poolRes.data.products || poolRes.data;
        if (Array.isArray(poolItems)) setSuggestionPool(poolItems);

        await fetchProducts(1, "All", "", true);
      } catch (err) {
        console.error("Shop Load Error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchProducts = async (pageNum, cat, searchQuery, reset = false) => {
    try {
      if (reset) {
        const safeCategory = encodeURIComponent(cat);
        const safeSearch = encodeURIComponent(searchQuery);

        const res = await API.get(`/products?limit=200&category=${safeCategory}&search=${safeSearch}&isVisible=true`);
        let fetchedProducts = res.data.products || res.data; 

        masterPoolRef.current = fetchedProducts;
        
        if (cat === "All" && !searchQuery) {
          setProducts(fetchedProducts); 
          setHasMore(false); 
        } else {
          setProducts(fetchedProducts.slice(0, 12));
          setHasMore(fetchedProducts.length > 12);
        }
      } else {
        const pool = masterPoolRef.current;
        const nextItems = pool.slice(0, pageNum * 12);
        setProducts(nextItems);
        setHasMore(pool.length > pageNum * 12);
      }
      setLoading(false);
    } catch (err) {
      console.error("Fetch Products Error", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProducts(1, category, search, true);
  }, [category, search]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, category, search, false);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search).get("search");
    setSearch(query || "");
    if(query) setCategory("All"); 
  }, [location.search]);

  const handleClearSearch = () => {
    setSearch("");
    setCategory("All");
    navigate(location.pathname); 
  };

  const handleProductClick = (p) => {
    setSelectedProduct(p); 
    API.patch(`/products/${p._id}/view`).catch(err => console.log("View log failed"));
  };

  const handleBannerClick = (index) => {
    vibrate(30); 
    const clickedBanner = banners[index];
    
    if (clickedBanner && clickedBanner.link && clickedBanner.link !== "/" && clickedBanner.link !== "All") {
       setCategory(clickedBanner.link);
       setSearch(""); 
       navigate(location.pathname); 
    } else {
       setCategory("All"); 
    }

    if (feedRef.current) {
      const yOffset = -90; 
      const y = feedRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const addToCart = (product) => {
    if (!isStoreOpen) { vibrate([100, 50, 100]); return toast.error("Store is Closed!"); }
    if (product.stock <= 0) { vibrate([100, 50, 100]); return toast.error("Out of Stock!"); }

    const existing = cart.find(item => item._id === product._id);
    if (existing && existing.quantity >= product.stock) {
      vibrate([100, 50, 100]); return toast.error(`Only ${product.stock} available!`);
    }

    let newCart;
    if (existing) {
      newCart = cart.map(item => item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item);
      setCart(newCart); toast.success(`Added another ${product.name}!`);
    } else {
      newCart = [...cart, { ...product, quantity: 1 }];
      setCart(newCart); toast.success(`${product.name} added!`);
    }
    vibrate(50); 
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  const calculateComboStock = (combo) => {
    let minStock = Infinity;
    for (let item of combo.items) {
        if (!item.product) continue;
        const available = Math.floor(item.product.stock / item.quantity);
        if (available < minStock) minStock = available;
    }
    return minStock === Infinity ? 0 : minStock;
  };

  const addComboToCart = (combo) => {
    if (!isStoreOpen) { vibrate([100, 50, 100]); return toast.error("Store is Closed!"); }
    
    const availableStock = calculateComboStock(combo);
    if (availableStock <= 0) { vibrate([100, 50, 100]); return toast.error("Combo is Out of Stock!"); }
    
    const existing = cart.find(item => item._id === combo._id);
    if (existing && existing.quantity >= availableStock) {
        vibrate([100, 50, 100]); return toast.error(`Only ${availableStock} available!`);
    }

    let newCart;
    if (existing) {
        newCart = cart.map(item => item._id === combo._id ? { ...item, quantity: item.quantity + 1 } : item);
        toast.success(`Added another ${combo.name}!`);
    } else {
        newCart = [...cart, { 
           _id: combo._id, 
           name: "🎁 " + combo.name, 
           price: combo.comboPrice, 
           image: combo.image, 
           stock: availableStock, 
           isCombo: true,
           comboItems: combo.items,
           quantity: 1 
        }];
        toast.success(`${combo.name} added!`);
    }
    setCart(newCart);
    vibrate(50);
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  const getQty = (id) => cart.find(i => i._id === id)?.quantity || 0;

  const updateQtyInDrawer = (product, change) => {
    if (!isStoreOpen && change > 0) { vibrate([100, 50, 100]); return toast.error("Store is Closed!"); }
    
    const existingItem = cart.find((item) => item._id === product._id);
    if (change > 0 && existingItem && existingItem.quantity >= product.stock) {
      vibrate([100, 50, 100]); return toast.error(`Only ${product.stock} available!`);
    }

    if (existingItem) {
      const newCart = cart.map((item) => item._id === product._id ? { ...item, quantity: Math.max(0, item.quantity + change) } : item).filter((item) => item.quantity > 0);
      setCart(newCart);
      if (change > 0) vibrate(50);
      if (change < 0) vibrate(30);
      localStorage.setItem("cart", JSON.stringify(newCart));
      if (newCart.length === 0) setShowDrawer(false); 
    }
  };
  
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalSellingPrice = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const amountNeededForFreeDelivery = 150 - totalSellingPrice;

  useEffect(() => {
    if (showDrawer && suggestionPool.length > 0) {
      const cartItemIds = cart.map(item => item._id);
      const validProducts = suggestionPool.filter(p => !cartItemIds.includes(p._id) && p.stock > 0);
      const perfectlyShuffled = shuffleArray(validProducts);
      setSuggestedProducts(perfectlyShuffled.slice(0, 8)); 
    }
  }, [showDrawer, suggestionPool]); 

  const displaySuggestions = suggestedProducts.filter(p => !cart.find(item => item._id === p._id));
  const suggestionsRef = useRef(null);
  
  useEffect(() => {
    let interval;
    if (showDrawer && isSuggestionsVisible && displaySuggestions.length > 0) {
      interval = setInterval(() => {
        if (suggestionsRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = suggestionsRef.current;
          if (scrollLeft + clientWidth >= scrollWidth - 5) {
            suggestionsRef.current.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            suggestionsRef.current.scrollBy({ left: 122, behavior: "smooth" });
          }
        }
      }, 3000); 
    }
    return () => clearInterval(interval);
  }, [showDrawer, isSuggestionsVisible, displaySuggestions.length]);

  const groupedData = useMemo(() => {
    if (search || category !== "All") return null;
    
    const groupedArray = [];
    const activeCategories = shuffleArray([...new Set(products.map(p => p.category))]);

    activeCategories.forEach(cat => {
      const itemsInCategory = products.filter(p => p.category === cat);
      if (itemsInCategory.length > 0) {
        groupedArray.push({
          category: cat,
          items: shuffleArray(itemsInCategory).slice(0, 6) 
        });
      }
    });

    return groupedArray;
  }, [products, search, category]);

  return (
    <div className="min-h-screen bg-white pb-32 font-sans selection:bg-green-100 overflow-x-hidden">
      <Toaster position="bottom-center" />

      {!isStoreOpen && (
        <div className="bg-red-600 text-white text-center py-3 font-bold sticky top-[60px] md:top-[80px] z-[50] shadow-md">
          🛑 Sorry, the store is currently CLOSED. We are not accepting orders.
        </div>
      )}

      <div className="max-w-6xl mx-auto pt-[60px] md:pt-[80px]">
        
        {search && (
          <div className="mb-2 px-3 md:px-4 mt-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center bg-green-50/70 border border-green-100 p-3 rounded-xl shadow-sm">
              <h2 className="text-sm font-bold text-gray-700">
                Results for <span className="text-green-700">"{search}"</span>
              </h2>
              <button 
                onClick={handleClearSearch} 
                className="w-7 h-7 bg-white rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 shadow-sm border border-gray-200 text-xs font-black transition-all"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {!loading && banners.length > 0 && !search && category === "All" && (
          <div className="mb-4 px-3 md:px-4 mt-3">
            <div className="rounded-2xl overflow-hidden shadow-sm">
              <Carousel 
                showThumbs={false} 
                autoPlay 
                infiniteLoop 
                interval={4000} 
                showStatus={false} 
                showArrows={false} 
                showIndicators={true} 
                stopOnHover={true} 
                swipeable={true} 
                emulateTouch={true}
                onClickItem={handleBannerClick}
                className="cursor-pointer active:scale-[0.98] transition-transform" 
              >
                {banners.map((b) => (<div key={b._id} className="h-40 md:h-72 w-full bg-gray-200"><img src={b.image} alt="Promo" className="w-full h-full object-cover" /></div>))}
              </Carousel>
            </div>
          </div>
        )}

        <div ref={feedRef}>
          {loading ? (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-4 px-3 md:px-4 mt-4">
              {[...Array(9)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="text-6xl mb-4 grayscale opacity-50">🍃</div>
              <p className="text-lg font-medium text-gray-500">No items found.</p>
              <button onClick={handleClearSearch} className="mt-4 px-6 py-2 bg-green-50 text-green-700 rounded-full font-bold">Clear Filters</button>
            </div>
          ) : groupedData ? (
            
            <div className="space-y-4 pb-10 mt-2">
              
              {/* SMART COMBOS SWIMLANE */}
              {combos.length > 0 && (
                <div className="flex flex-col mb-6 bg-orange-50/30 py-4 border-y border-orange-100">
                  <div className="flex justify-between items-end mb-3 px-4">
                    <h2 className="text-[22px] md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-500 tracking-tight leading-none">
                      🔥 Smart Combos
                    </h2>
                  </div>
                  
                  <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 pt-1 px-4">
                    {combos.map(combo => {
                      const comboStock = calculateComboStock(combo);
                      return (
                        <div key={combo._id} className="snap-start shrink-0 w-[260px] md:w-[300px] bg-white rounded-2xl border border-orange-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow relative">
                          {comboStock <= 0 && <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center font-black text-xl text-red-600 tracking-widest">SOLD OUT</div>}
                          <div className="relative h-32 bg-gray-100 shrink-0">
                            <img src={combo.image} alt={combo.name} className="w-full h-full object-cover" />
                            <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-black px-2.5 py-1 rounded shadow-md uppercase tracking-wider">
                                SAVE ₹{combo.originalPrice - combo.comboPrice}
                            </div>
                          </div>
                          <div className="p-3 flex flex-col flex-1">
                            <h3 className="font-extrabold text-gray-900 text-sm mb-2 leading-tight line-clamp-1">{combo.name}</h3>
                            
                            {/* 🟢 FIXED: Multiply Price by Quantity for total line item cost */}
                            <div className="bg-orange-50/50 rounded-lg p-2 mb-3 border border-orange-100/50 flex-1 overflow-y-auto scrollbar-hide max-h-[100px]">
                               <span className="text-[9px] font-black text-orange-800 uppercase tracking-widest block mb-1.5 sticky top-0 bg-orange-50/90 backdrop-blur-sm z-10 pb-1">📦 Bundle Includes:</span>
                               <div className="space-y-1.5">
                                 {combo.items.map((i, idx) => (
                                   <div key={idx} className="flex items-center gap-2 bg-white/60 p-1 rounded-md border border-orange-100/50">
                                     <img src={i.product?.image} className="w-7 h-7 rounded bg-white object-cover shadow-sm shrink-0" alt="" />
                                     <div className="flex-1 min-w-0 flex justify-between items-center text-[10px]">
                                       <span className="font-bold text-gray-800 truncate pr-1">{i.quantity}x {i.product?.name}</span>
                                       <span className="text-gray-500 font-semibold shrink-0">₹{(i.product?.price || 0) * i.quantity}</span>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                            </div>

                            <div className="mt-auto flex items-center justify-between pt-1">
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-400 line-through font-bold">₹{combo.originalPrice}</span>
                                  <span className="text-lg font-black text-red-600 leading-none">₹{combo.comboPrice}</span>
                                </div>
                                
                                {getQty(combo._id) > 0 ? (
                                    <div className="flex items-center bg-white rounded-lg h-8 px-2 gap-3 border border-orange-300 shadow-sm z-20">
                                      <button onClick={() => updateQtyInDrawer({_id: combo._id, stock: comboStock}, -1)} className="text-lg font-bold text-orange-600 w-6 flex justify-center">-</button>
                                      <span className="text-sm font-bold text-gray-800">{getQty(combo._id)}</span>
                                      <button onClick={() => updateQtyInDrawer({_id: combo._id, stock: comboStock}, 1)} className="text-lg font-bold text-orange-600 w-6 flex justify-center">+</button>
                                    </div>
                                ) : (
                                    <button onClick={() => addComboToCart(combo)} disabled={comboStock <= 0} className="bg-red-600 text-white font-bold text-xs px-5 py-2 rounded-lg shadow-sm hover:bg-red-700 transition-colors active:scale-95 disabled:bg-gray-300 disabled:text-gray-500 z-20">
                                      + ADD
                                    </button>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DYNAMIC SHUFFLED CATEGORIES */}
              {groupedData.map((group) => (
                <div key={group.category} className="flex flex-col">
                  <div className="flex justify-between items-end mb-3 px-4">
                    <h2 className="text-[22px] md:text-2xl font-extrabold text-gray-900 tracking-tight leading-none">{group.category}</h2>
                    <button onClick={() => setCategory(group.category)} className="text-green-800 font-bold text-xs bg-green-50/80 px-3 py-1.5 rounded-full hover:bg-green-100 transition-colors flex items-center gap-1 border border-green-100">
                      See All <span className="text-sm leading-none">➔</span>
                    </button>
                  </div>
                  
                  <div className="flex gap-2.5 md:gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 pt-1 px-3 md:px-4">
                    {group.items.map((p) => (
                      <div key={p._id} className="snap-start shrink-0 w-[115px] sm:w-[140px] md:w-[180px]" onClick={() => handleProductClick(p)}>
                        <ProductCard product={p} cart={cart} setCart={setCart} />
                      </div>
                    ))}
                    
                    <div onClick={() => setCategory(group.category)} className="snap-start shrink-0 w-[115px] sm:w-[140px] md:w-[180px] flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-green-600 shadow-sm mb-2 font-bold text-xl">➔</div>
                      <span className="font-bold text-gray-700 text-sm">View all</span>
                      <span className="font-medium text-gray-500 text-[10px] sm:text-xs mt-0.5 text-center px-2">{group.category}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-3 md:px-4 pb-10">
              <div className="flex items-center justify-between mb-4 mt-2">
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
                   {category === "All" ? "Search Results" : category}
                </h2>
                {category !== "All" && (
                  <button onClick={() => setCategory("All")} className="text-sm font-bold text-gray-600 border border-gray-200 hover:border-gray-400 bg-white px-3 py-1.5 rounded-full shadow-sm transition-all">
                    ← Back
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-4">
                {products.map((p) => (
                  <div key={p._id} onClick={() => handleProductClick(p)} className="cursor-pointer">
                    <ProductCard product={p} cart={cart} setCart={setCart} />
                  </div>
                ))}
              </div>

              {hasMore && products.length > 0 && (
                <div className="mt-10 text-center">
                  <button onClick={handleLoadMore} className="bg-white border border-green-600 text-green-700 px-8 py-3 rounded-full font-bold hover:bg-green-50 transition-colors shadow-sm active:scale-95">
                    Load More Products ↓
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PRODUCT POPUP */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedProduct(null)}></div>
          <div className="relative w-full md:w-[450px] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in slide-in-from-bottom duration-300">
            <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 z-10 bg-white/80 rounded-full p-2 shadow-sm font-bold hover:bg-gray-100">✕</button>
            
            <div className="h-64 bg-gray-50 flex items-center justify-center relative p-4 border-b border-gray-100">
              <img src={selectedProduct.image} alt={selectedProduct.name} loading="lazy" className="h-full w-auto object-contain mix-blend-multiply" />
              {selectedProduct.stock <= 0 && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-2xl backdrop-blur-sm">SOLD OUT</div>}
            </div>

            <div className="p-6 pb-24 overflow-y-auto">
              <h2 className="text-2xl font-extrabold text-gray-900 pr-4 leading-tight">{selectedProduct.name}</h2>
              <div className="flex items-center gap-3 mt-3">
                <div className="text-2xl font-black text-gray-900">₹{selectedProduct.price}</div>
                {selectedProduct.mrp > selectedProduct.price && <div className="text-lg text-gray-400 line-through font-medium">₹{selectedProduct.mrp}</div>}
                {selectedProduct.mrp > selectedProduct.price && (
                  <div className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded">
                    {Math.round(((selectedProduct.mrp - selectedProduct.price) / selectedProduct.mrp) * 100)}% OFF
                  </div>
                )}
              </div>
              <p className="text-gray-500 mt-4 text-sm leading-relaxed">{selectedProduct.description || "Fresh, high-quality product delivered straight to your door in Goverdhan Enclave."}</p>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
              {isStoreOpen ? (
                selectedProduct.stock > 0 ? (
                    getQty(selectedProduct._id) > 0 ? (
                      <div className="flex items-center justify-between gap-4">
                         <div className="flex items-center bg-gray-50 rounded-xl h-14 px-4 gap-6 flex-1 justify-between border border-gray-200">
                           <button onClick={() => updateQtyInDrawer(selectedProduct, -1)} className="text-2xl font-bold text-green-700 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200">-</button>
                           <span className="text-xl font-bold text-gray-800">{getQty(selectedProduct._id)}</span>
                           <button onClick={() => updateQtyInDrawer(selectedProduct, 1)} className="text-2xl font-bold text-green-700 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200">+</button>
                         </div>
                         <button onClick={() => { setSelectedProduct(null); setShowDrawer(true); setIsSuggestionsVisible(true); }} className="bg-green-700 text-white h-14 px-6 rounded-xl font-bold shadow-lg hover:bg-green-800 transition-colors">View Cart ➔</button>
                      </div>
                    ) : (
                      <button onClick={() => addToCart(selectedProduct)} className="w-full bg-green-700 text-white h-14 rounded-xl font-bold text-lg shadow-lg hover:bg-green-800 active:scale-[0.98] transition-all">ADD TO CART</button>
                    )
                ) : (
                    <button disabled className="w-full bg-gray-100 text-gray-400 h-14 rounded-xl font-bold text-lg cursor-not-allowed border border-gray-200">Out of Stock</button>
                )
              ) : (
                <button disabled className="w-full bg-gray-400 text-white h-14 rounded-xl font-bold cursor-not-allowed">STORE CLOSED</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Cart Bar */}
      {cart.length > 0 && !showDrawer && !selectedProduct && (
        <div className="fixed bottom-4 left-4 right-4 z-40 animate-in slide-in-from-bottom-10 duration-500">
          <div onClick={() => { setShowDrawer(true); setIsSuggestionsVisible(true); }} className="bg-green-800 text-white p-4 rounded-2xl shadow-[0_10px_30px_rgba(22,163,74,0.3)] flex justify-between items-center cursor-pointer border border-green-700 max-w-md mx-auto">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-green-200 uppercase tracking-widest">{cartCount} ITEM{cartCount > 1 ? 'S' : ''}</span>
              <span className="text-xl font-extrabold tracking-tight">₹{totalSellingPrice}</span>
            </div>
            <div className="flex items-center gap-2 font-bold bg-white text-green-800 px-5 py-2.5 rounded-xl text-sm shadow-sm hover:bg-gray-50">View Cart 🛍️</div>
          </div>
        </div>
      )}
      
      {/* DRAWER */}
      {showDrawer && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm" onClick={() => setShowDrawer(false)} />
          <div className="fixed bottom-0 left-0 right-0 md:left-auto md:top-0 md:w-[400px] bg-white z-[110] rounded-t-3xl md:rounded-none md:rounded-l-3xl shadow-2xl max-h-[85vh] md:max-h-screen md:h-full flex flex-col animate-in slide-in-from-bottom md:slide-in-from-right">
            
            <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-white rounded-t-3xl md:rounded-none z-10 shrink-0">
              <h3 className="text-xl font-extrabold text-gray-800">Your Cart 🛒</h3>
              <button onClick={() => setShowDrawer(false)} className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200 font-bold">✕</button>
            </div>

            <div className="bg-blue-50/50 p-3 text-center border-b border-blue-100 z-10 shrink-0">
              {amountNeededForFreeDelivery <= 0 ? (
                <div className="text-green-600 font-bold text-sm">🎉 You've unlocked FREE Delivery!</div>
              ) : (
                <div className="text-blue-800 font-bold text-sm">🚚 Add <span className="text-red-500">₹{amountNeededForFreeDelivery}</span> more for FREE Delivery! (Or pay ₹10)</div>
              )}
            </div>

            <div className="p-5 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-5">
                {cart.map((item) => (
                  <div key={item._id} className="flex justify-between items-start bg-white">
                    <div className="flex items-start gap-3 flex-1 pr-2">
                       <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover bg-gray-50 border border-gray-100 shrink-0" />
                       <div className="flex flex-col w-full">
                         <div className="font-bold text-gray-800 line-clamp-1 text-sm">{item.name}</div>
                         
                         {/* 🟢 FIXED: Multiply Price by Quantity in Cart Combo Line Items */}
                         {item.isCombo && item.comboItems && (
                           <div className="mt-2 mb-1 pl-2 border-l-2 border-green-200 space-y-1.5 w-full">
                             {item.comboItems.map((i, idx) => (
                               <div key={idx} className="flex items-center gap-2">
                                 <img src={i.product?.image} className="w-5 h-5 rounded object-cover border border-gray-100 shrink-0 bg-gray-50" alt="" />
                                 <div className="text-[10px] text-gray-600 flex-1 flex justify-between min-w-0">
                                   <span className="truncate pr-1"><strong className="text-gray-800">{i.quantity}x</strong> {i.product?.name}</span>
                                   <span className="shrink-0 font-medium">₹{(i.product?.price || 0) * i.quantity}</span>
                                 </div>
                               </div>
                             ))}
                           </div>
                         )}

                         <div className="text-xs text-gray-500 font-medium mt-0.5">₹{item.price} x {item.quantity} = <strong className="text-gray-800">₹{item.price * item.quantity}</strong></div>
                       </div>
                    </div>
                    <div className="flex items-center bg-green-50 border border-green-200 rounded-lg shrink-0 h-9">
                      <button onClick={() => updateQtyInDrawer(item, -1)} className="w-8 h-full flex items-center justify-center text-green-700 font-bold hover:bg-green-100 rounded-l-lg">-</button>
                      <span className="px-1 text-sm font-bold w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQtyInDrawer(item, 1)} className="w-8 h-full flex items-center justify-center text-green-700 font-bold hover:bg-green-100 rounded-r-lg">+</button>
                    </div>
                  </div>
                ))}
              </div>

              {isSuggestionsVisible && displaySuggestions.length > 0 && (
                <div className="mt-8 bg-gray-50 border border-gray-100 rounded-2xl p-4 relative shadow-sm overflow-hidden">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider pr-6">Customers also bought:</h4>
                    <button onClick={() => setIsSuggestionsVisible(false)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 bg-white shadow-sm rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold transition-all z-10">✕</button>
                  </div>
                  <div ref={suggestionsRef} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x pb-2 pt-1 scroll-smooth">
                    {displaySuggestions.map(sp => (
                      <div key={sp._id} className="min-w-[110px] w-[110px] bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex flex-col snap-start shrink-0 hover:shadow-md transition-shadow">
                        <img src={sp.image} alt={sp.name} className="w-full h-16 object-contain rounded-lg mb-2 bg-gray-50 mix-blend-multiply" />
                        <div className="text-[11px] font-bold text-gray-800 line-clamp-2 leading-tight flex-1 mb-1">{sp.name}</div>
                        <div className="text-xs font-extrabold text-green-700 mb-2">₹{sp.price}</div>
                        <button onClick={() => addToCart(sp)} className="w-full bg-white text-green-700 border border-green-200 shadow-sm text-[10px] font-bold py-1.5 rounded-lg hover:bg-green-50 active:bg-green-100 transition-colors">+ ADD</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 bg-white border-t border-gray-200 shadow-[0_-10px_20px_rgba(0,0,0,0.03)] z-10 shrink-0 pb-safe">
               {isStoreOpen ? (
                 <Link to="/checkout" onClick={() => setShowDrawer(false)}>
                   <button className="w-full bg-green-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg flex justify-between px-6 items-center hover:bg-green-800 active:scale-[0.98] transition-all">
                     <span>Checkout</span>
                     <span className="flex items-center gap-2">₹{totalSellingPrice} ➔</span>
                   </button>
                 </Link>
               ) : (
                 <button disabled className="w-full bg-gray-400 text-white py-4 rounded-xl font-bold text-lg cursor-not-allowed">Store Closed</button>
               )}
            </div>
          </div>
        </>
      )}
      <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
}

export default Shop;